/**
 * Dimensiones contables — Finance 360 (ítem 117).
 *
 * ── LO QUE ESTA PANTALLA DESTRABA ───────────────────────────────────────────
 *
 * El modelo, la migración y las puertas del catálogo existían desde el ítem 114,
 * pero no había dónde usarlos: el catálogo tenía cuatro puertas y **ninguna era
 * de alta**. Un eje solo podía nacer empujado desde otro producto, nunca creado
 * por una persona.
 *
 * ── LAS DOS COSAS QUE LA PANTALLA TIENE QUE DEJAR CLARAS ────────────────────
 *
 * **De dónde salen los valores.** Un eje toma los suyos de este catálogo o de
 * una tabla que se sincroniza desde otro producto. No se editan igual, y
 * confundirlos es prometer un cambio que el próximo sincronizado va a pisar.
 *
 * **La vigencia no es el estado.** Un centro de costo que cerró sigue siendo
 * válido para los asientos anteriores: por eso un valor con fecha de cierre se
 * muestra como «cerrado el …» y no como inactivo, que son cosas distintas.
 */

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  Layers, Plus, Pencil, Trash2, X, ChevronRight, ArrowLeft,
  Lock, CalendarOff, AlertTriangle
} from 'lucide-react';
import { contabilidadApi } from '../../utils/api';
import LoadingSpinner from '../../components/LoadingSpinner';

/** El catálogo propio: el resto de los valores llega sincronizado. */
const CATALOGO_PROPIO = 'dimension_valores';
const esPropio = (eje) => !eje?.tablaValores || eje.tablaValores === CATALOGO_PROPIO;

const OBLIGATORIEDAD = {
  ninguna: 'Ninguna',
  resultado: 'Cuentas de resultado',
  todas: 'Todas las cuentas'
};

/** El mensaje del servidor es el que explica la regla; no se reemplaza por uno genérico. */
const avisar = (e, siNo) => toast.error(e?.response?.data?.error || e?.message || siNo);

function ModalEje({ eje, onCerrar, onGuardado }) {
  const nuevo = !eje;
  const [datos, setDatos] = useState({
    codigo: eje?.codigo || '',
    nombre: eje?.nombre || '',
    descripcion: eje?.descripcion || '',
    obligatoriaEn: eje?.obligatoriaEn || 'ninguna'
  });
  const [guardando, setGuardando] = useState(false);

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      if (nuevo) await contabilidadApi.createDimension(datos);
      else await contabilidadApi.updateDimension(eje.id, { ...datos, codigo: undefined });
      toast.success(nuevo ? 'Eje creado' : 'Eje actualizado');
      onGuardado();
    } catch (err) {
      avisar(err, 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form onSubmit={guardar} className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold text-lg">{nuevo ? 'Nuevo eje' : `Editar ${eje.codigo}`}</h3>
          <button type="button" onClick={onCerrar} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Código</label>
            <input
              value={datos.codigo}
              onChange={(e) => setDatos({ ...datos, codigo: e.target.value.toUpperCase() })}
              disabled={!nuevo}
              placeholder="FINCA"
              className="w-full border rounded-lg px-3 py-2 font-mono disabled:bg-gray-50 disabled:text-gray-500"
            />
            {/* El código viaja como llave entre los tres productos: por eso no admite
                espacios ni acentos, y por eso no se cambia después. */}
            <p className="text-xs text-gray-500 mt-1">
              {nuevo
                ? 'Letras sin acento, números y guion bajo. Viaja como llave hacia Unity y Payroll.'
                : 'El código no se cambia: queda escrito en los asientos.'}
            </p>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Nombre</label>
            <input
              value={datos.nombre}
              onChange={(e) => setDatos({ ...datos, nombre: e.target.value })}
              placeholder="Finca"
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Obligatoria en</label>
            <select
              value={datos.obligatoriaEn}
              onChange={(e) => setDatos({ ...datos, obligatoriaEn: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
            >
              {Object.entries(OBLIGATORIEDAD).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
            {/* Exigirla en TODAS bloquea asientos de banco y patrimonio donde no
                tiene sentido. «Cuentas de resultado» es lo que casi siempre se quiere. */}
            <p className="text-xs text-gray-500 mt-1">
              Exigirla en todas las cuentas bloquea asientos de banco y patrimonio, donde no aplica.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50 rounded-b-xl">
          <button type="button" onClick={onCerrar} className="px-4 py-2 border rounded-lg text-sm">Cancelar</button>
          <button type="submit" disabled={guardando} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ModalValor({ eje, valor, onCerrar, onGuardado }) {
  const nuevo = !valor;
  const [datos, setDatos] = useState({
    codigo: valor?.codigo || '',
    nombre: valor?.nombre || '',
    vigenteDesde: valor?.vigenteDesde || '',
    vigenteHasta: valor?.vigenteHasta || '',
    permiteMovimiento: valor ? valor.permiteMovimiento : true
  });
  const [guardando, setGuardando] = useState(false);

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const cuerpo = {
        ...datos,
        vigenteDesde: datos.vigenteDesde || null,
        vigenteHasta: datos.vigenteHasta || null
      };
      if (nuevo) await contabilidadApi.createDimensionValor(eje.id, cuerpo);
      else await contabilidadApi.updateDimensionValor(valor.id, { ...cuerpo, codigo: undefined });
      toast.success(nuevo ? 'Valor agregado' : 'Valor actualizado');
      onGuardado();
    } catch (err) {
      avisar(err, 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form onSubmit={guardar} className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold text-lg">{nuevo ? `Nuevo valor de ${eje.nombre}` : `Editar ${valor.codigo}`}</h3>
          <button type="button" onClick={onCerrar} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Código</label>
              <input
                value={datos.codigo}
                onChange={(e) => setDatos({ ...datos, codigo: e.target.value.toUpperCase() })}
                disabled={!nuevo}
                className="w-full border rounded-lg px-3 py-2 font-mono disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Nombre</label>
              <input
                value={datos.nombre}
                onChange={(e) => setDatos({ ...datos, nombre: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Vigente desde</label>
              <input type="date" value={datos.vigenteDesde}
                onChange={(e) => setDatos({ ...datos, vigenteDesde: e.target.value })}
                className="w-full border rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Vigente hasta</label>
              <input type="date" value={datos.vigenteHasta}
                onChange={(e) => setDatos({ ...datos, vigenteHasta: e.target.value })}
                className="w-full border rounded-lg px-3 py-2" />
            </div>
          </div>
          {/* La vigencia es lo que permite cerrar algo sin invalidar su historia:
              los asientos anteriores a la fecha de cierre siguen siendo válidos. */}
          <p className="text-xs text-gray-500 -mt-2">
            Vacías = vigente siempre. Al imputar se ofrecen los vigentes <b>a la fecha del asiento</b>,
            así que cerrar un valor no invalida lo que ya se registró con él.
          </p>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={datos.permiteMovimiento}
              onChange={(e) => setDatos({ ...datos, permiteMovimiento: e.target.checked })} />
            Admite movimiento
            <span className="text-xs text-gray-500">— desmarcado sirve para agrupar, no para imputarle</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50 rounded-b-xl">
          <button type="button" onClick={onCerrar} className="px-4 py-2 border rounded-lg text-sm">Cancelar</button>
          <button type="submit" disabled={guardando} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Dimensiones() {
  const [ejes, setEjes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [abierto, setAbierto] = useState(null);      // el eje cuyo detalle se mira
  const [valores, setValores] = useState([]);
  const [cargandoValores, setCargandoValores] = useState(false);
  const [modalEje, setModalEje] = useState(null);    // { eje } o { eje: null } para nuevo
  const [modalValor, setModalValor] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const { data } = await contabilidadApi.getDimensiones(true);
      setEjes(data.dimensiones || []);
    } catch (e) {
      avisar(e, 'No se pudieron cargar las dimensiones');
    } finally {
      setCargando(false);
    }
  };

  const cargarValores = async (eje) => {
    setCargandoValores(true);
    try {
      // Sin `aFecha` a propósito: quien administra el catálogo tiene que ver
      // también los cerrados, que es lo que no se ve al imputar.
      const { data } = await contabilidadApi.getDimensionCatalogo(eje.id, { incluirInactivos: 'true' });
      setValores(data.valores || []);
    } catch (e) {
      avisar(e, 'No se pudieron cargar los valores');
      setValores([]);
    } finally {
      setCargandoValores(false);
    }
  };

  useEffect(() => { cargar(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `cargarValores` se redefine en cada render; la dependencia real es el eje abierto
  useEffect(() => { if (abierto) cargarValores(abierto); }, [abierto]);

  const borrarEje = async (eje) => {
    try {
      await contabilidadApi.deleteDimension(eje.id);
      toast.success('Eje eliminado');
      cargar();
    } catch (e) { avisar(e, 'No se pudo eliminar'); }
  };

  const borrarValor = async (v) => {
    try {
      await contabilidadApi.deleteDimensionValor(v.id);
      toast.success('Valor eliminado');
      cargarValores(abierto);
    } catch (e) { avisar(e, 'No se pudo eliminar'); }
  };

  if (cargando) return <LoadingSpinner />;

  // ── El detalle de un eje ──────────────────────────────────────────────────
  if (abierto) {
    const propio = esPropio(abierto);
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <button onClick={() => setAbierto(null)} className="flex items-center gap-1 text-sm text-gray-600 mb-4">
          <ArrowLeft size={16} /> Volver a dimensiones
        </button>
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-2xl font-bold">{abierto.nombre}</h1>
          <span className="font-mono text-sm text-gray-500">{abierto.codigo}</span>
        </div>

        {!propio && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 my-4 text-sm">
            <Lock size={16} className="mt-0.5 shrink-0" />
            <div>
              El eje es de Finance, pero <b>sus valores llegan sincronizados</b> desde otro producto.
              Acá se consultan; para agregarlos o renombrarlos se entra donde se crean.
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mt-6 mb-3">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Valores</h2>
          {propio && (
            <button onClick={() => setModalValor({ valor: null })}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm">
              <Plus size={15} /> Nuevo valor
            </button>
          )}
        </div>

        {cargandoValores ? <LoadingSpinner /> : (
          <div className="bg-white border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2">Código</th>
                  <th className="text-left px-4 py-2">Nombre</th>
                  <th className="text-left px-4 py-2">Vigencia</th>
                  <th className="text-left px-4 py-2">Movimiento</th>
                  {propio && <th className="px-4 py-2" />}
                </tr>
              </thead>
              <tbody>
                {valores.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                    Este eje todavía no tiene valores.
                  </td></tr>
                )}
                {valores.map((v) => (
                  <tr key={v.id} className={`border-t ${v.estado !== 'activo' ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-2.5 font-mono">{v.codigo}</td>
                    <td className="px-4 py-2.5">{v.nombre}</td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {/* Un valor cerrado NO es un valor inactivo: sus asientos
                          anteriores siguen valiendo, y por eso se dice la fecha. */}
                      {v.vigenteHasta
                        ? <span className="inline-flex items-center gap-1"><CalendarOff size={13} /> cerrado el {v.vigenteHasta}</span>
                        : v.vigenteDesde ? `desde ${v.vigenteDesde}` : 'siempre'}
                    </td>
                    <td className="px-4 py-2.5">{v.permiteMovimiento ? 'Sí' : 'Solo agrupa'}</td>
                    {propio && (
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <button onClick={() => setModalValor({ valor: v })} title="Editar"
                          className="text-gray-400 hover:text-blue-600 mr-3"><Pencil size={15} /></button>
                        <button onClick={() => borrarValor(v)} title="Eliminar"
                          className="text-gray-400 hover:text-red-600"><Trash2 size={15} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {modalValor && (
          <ModalValor eje={abierto} valor={modalValor.valor}
            onCerrar={() => setModalValor(null)}
            onGuardado={() => { setModalValor(null); cargarValores(abierto); }} />
        )}
      </div>
    );
  }

  // ── La lista de ejes ──────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Layers size={22} /> Dimensiones contables
        </h1>
        <button onClick={() => setModalEje({ eje: null })}
          className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm">
          <Plus size={16} /> Nuevo eje
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-6 max-w-2xl">
        Un eje es una forma de cortar la contabilidad: centro de costo, finca, proyecto, línea de
        negocio. Son pocos por naturaleza.
      </p>

      {ejes.length === 0 ? (
        <div className="border border-dashed rounded-xl py-16 text-center text-gray-500">
          <AlertTriangle size={22} className="mx-auto mb-2 text-gray-400" />
          Todavía no hay dimensiones configuradas.
        </div>
      ) : (
        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Código</th>
                <th className="text-left px-4 py-2">Nombre</th>
                <th className="text-left px-4 py-2">Obligatoria en</th>
                <th className="text-left px-4 py-2">Valores desde</th>
                <th className="text-left px-4 py-2">Estado</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {ejes.map((e) => (
                <tr key={e.id} className={`border-t hover:bg-gray-50 ${e.estado !== 'activo' ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3 font-mono">{e.codigo}</td>
                  <td className="px-4 py-3">{e.nombre}</td>
                  <td className="px-4 py-3 text-gray-600">{OBLIGATORIEDAD[e.obligatoriaEn] || e.obligatoriaEn}</td>
                  <td className="px-4 py-3">
                    {esPropio(e)
                      ? <span className="text-xs bg-violet-50 text-violet-700 rounded-full px-2 py-0.5">Este catálogo</span>
                      : <span className="text-xs bg-blue-50 text-blue-700 rounded-full px-2 py-0.5">Sincronizado</span>}
                  </td>
                  <td className="px-4 py-3">
                    {e.estado === 'activo'
                      ? <span className="text-xs bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5">Activo</span>
                      : <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">De baja</span>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setAbierto(e)} title="Ver valores"
                      className="text-gray-400 hover:text-blue-600 mr-3"><ChevronRight size={16} /></button>
                    <button onClick={() => setModalEje({ eje: e })} title="Editar"
                      className="text-gray-400 hover:text-blue-600 mr-3"><Pencil size={15} /></button>
                    <button onClick={() => borrarEje(e)} title="Eliminar"
                      className="text-gray-400 hover:text-red-600"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalEje && (
        <ModalEje eje={modalEje.eje}
          onCerrar={() => setModalEje(null)}
          onGuardado={() => { setModalEje(null); cargar(); }} />
      )}
    </div>
  );
}
